import { useCallback, useSyncExternalStore } from 'react';

export type DismissalKind = 'checklist' | 'agent';

export const dismissalKey = (userId: string, workspaceId: string, kind: DismissalKind): string =>
  `reportpad:onboarding:${userId}:${workspaceId}:${kind}`;

export interface DismissalState {
  checklist: boolean;
  agent: boolean;
  reopened: boolean;
  focusRequest: number;
}

interface Memory {
  reopened: boolean;
  focusRequest: number;
  lastFocusHandled: number;
  // Fallback flags, used when storage throws (private mode): they last for the session.
  checklist: boolean;
  agent: boolean;
  snapshot: DismissalState | null;
}

const NONE: DismissalState = { checklist: false, agent: false, reopened: false, focusRequest: 0 };
const memory = new Map<string, Memory>();
const listeners = new Set<() => void>();

const idOf = (userId: string, workspaceId: string): string => `${userId}:${workspaceId}`;

function memoryFor(id: string): Memory {
  let m = memory.get(id);
  if (!m) {
    m = { reopened: false, focusRequest: 0, lastFocusHandled: 0, checklist: false, agent: false, snapshot: null };
    memory.set(id, m);
  }
  return m;
}

function notify(): void {
  for (const l of [...listeners]) l();
}

function stored(userId: string, workspaceId: string, kind: DismissalKind): boolean {
  try {
    return localStorage.getItem(dismissalKey(userId, workspaceId, kind)) === '1';
  } catch {
    return false;
  }
}

export function dismiss(userId: string, workspaceId: string, kind: DismissalKind): void {
  const m = memoryFor(idOf(userId, workspaceId));
  m[kind] = true;
  try {
    localStorage.setItem(dismissalKey(userId, workspaceId, kind), '1');
  } catch {
    // Storage unavailable: the in-memory flag carries the dismissal for this page.
  }
  if (kind === 'checklist') m.reopened = false;
  notify();
}

export function reopen(userId: string, workspaceId: string): void {
  const m = memoryFor(idOf(userId, workspaceId));
  m.checklist = false;
  m.agent = false;
  try {
    localStorage.removeItem(dismissalKey(userId, workspaceId, 'checklist'));
    localStorage.removeItem(dismissalKey(userId, workspaceId, 'agent'));
  } catch {
    // Storage unavailable: nothing persisted to clear.
  }
  m.reopened = true;
  m.focusRequest += 1;
  notify();
}

/** True once per reopen: returns true and records it when focusRequest > lastFocusHandled. */
export function takeFocusRequest(userId: string, workspaceId: string): boolean {
  const m = memoryFor(idOf(userId, workspaceId));
  if (m.focusRequest > m.lastFocusHandled) {
    m.lastFocusHandled = m.focusRequest;
    return true;
  }
  return false;
}

function snapshotOf(userId: string, workspaceId: string): DismissalState {
  const m = memoryFor(idOf(userId, workspaceId));
  const next: DismissalState = {
    checklist: m.checklist || stored(userId, workspaceId, 'checklist'),
    agent: m.agent || stored(userId, workspaceId, 'agent'),
    reopened: m.reopened,
    focusRequest: m.focusRequest
  };
  const prev = m.snapshot;
  if (
    prev &&
    prev.checklist === next.checklist &&
    prev.agent === next.agent &&
    prev.reopened === next.reopened &&
    prev.focusRequest === next.focusRequest
  ) {
    return prev;
  }
  m.snapshot = next;
  return next;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** userId null (identity not loaded): all false, and storage is not read. */
export function useDismissal(userId: string | null, workspaceId: string): DismissalState {
  const getSnapshot = useCallback(
    (): DismissalState => (userId === null ? NONE : snapshotOf(userId, workspaceId)),
    [userId, workspaceId]
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
